import './CustomButton.css';

import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';

import { Button, CircularProgress, Tooltip } from '@mui/material';
import { generateClassName, shouldRenderComponent } from './helpers';
import { Cancel, Check } from '@mui/icons-material';
import B2BDialog from './B2BDialog';
import { LoginContext } from '../../App';
import useB2BTranslation from './hooks/useB2BTranslation';

const CustomButton = ({ label = null, tooltip = null, icon, isLoading, className, confirm = false, confirmDialog = null, onConfirm, disabledText = 'Disabled', rolesAllowed = [], b2bProducts = [], theme = null, ...props }) => {
    const { t } = useB2BTranslation('b2b-common');
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const { loginData } = useContext(LoginContext);

    const shouldRender = shouldRenderComponent(loginData, rolesAllowed, b2bProducts);

    if (!shouldRender) {
        return null;
    }

    const handleClick = () => {
        if (confirm) {
            setIsConfirmationOpen(true);
        } else {
            props.onClick && props.onClick();
        }
    };

    const handleConfirmation = () => {
        setIsConfirmationOpen(false);
        onConfirm && onConfirm();
    };

    const handleCancel = () => {
        setIsConfirmationOpen(false);
    };

    const renderIcon = () => {
        if (!icon) return null;
        const iconElement = React.cloneElement(icon, { className: `${generateClassName('custom-button', '-icon', className)}` });
        return tooltip ? <Tooltip title={tooltip}>{iconElement}</Tooltip> : iconElement;
    };

    return (
        <>
            <Button className={`${generateClassName("custom-button", "", className)} ${theme ? theme : ''}`}
                disabled={isLoading === true}
                onClick={handleClick}
                {...props}>
                {isLoading ? <CircularProgress size={15} color="inherit" className={generateClassName('custom-button', '-progress', className)} /> : renderIcon()}
                {label && (
                    <span className={generateClassName('custom-button', '-label', className)}>
                        {isLoading ? disabledText : label}
                    </span>
                )}
            </Button>
            <B2BDialog open={isConfirmationOpen} title={t('Confirmation Request')}
                onClose={() => setIsConfirmationOpen(false)}
                buttons={[
                    {
                        label: t('Confirm'),
                        onClick: handleConfirmation,
                        icon: <Check />
                    },
                    {
                        label: t('Cancel'),
                        onClick: handleCancel,
                        icon: <Cancel />
                    },
                ]}
            >
                <p>{confirmDialog ? confirmDialog : t("Are you sure you want to proceed with this action?")}</p>
            </B2BDialog>
        </>
    );
};

CustomButton.propTypes = {
    label: PropTypes.string,
    icon: PropTypes.element,
    isLoading: PropTypes.bool,
    className: PropTypes.string,
    confirm: PropTypes.bool,
    confirmDialog: PropTypes.string,
    onConfirm: PropTypes.func,
    disabledText: PropTypes.string,
    rolesAllowed: PropTypes.arrayOf(PropTypes.string),
    props: PropTypes.object
};

export default CustomButton;